/**
 * Fixed top banner shown while a platform admin is viewing a customer
 * organization in support mode. Highly visible (rot) — so der Admin nie
 * vergisst, in welcher Sicht er gerade ist, und so andere Personen, die
 * über die Schulter schauen, sofort erkennen, dass keine Spielwiese,
 * sondern echte Kundendaten zu sehen sind.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, LogOut, Loader2, Wrench } from 'lucide-react';
import { toast } from 'sonner';

import { useSupportMode } from '@/contexts/SupportModeContext';
import { PLATFORM_ADMIN_HOME } from '@/features/post-login-redirect';
import { SupportActionDialog } from '@/components/SupportActionDialog';

export function SupportModeBanner() {
  const { active, session, endSupport } = useSupportMode();
  const navigate = useNavigate();
  const [ending, setEnding] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);

  if (!active || !session) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      await endSupport();
      navigate(PLATFORM_ADMIN_HOME, { replace: true });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Beenden fehlgeschlagen.';
      toast.error(message);
    } finally {
      setEnding(false);
    }
  };

  return (
    <>
      <div
        role="status"
        aria-live="polite"
        className="sticky top-0 z-50 bg-destructive text-destructive-foreground px-4 py-2 flex items-center justify-between gap-3 text-xs shadow-md"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ShieldAlert aria-hidden="true" className="w-4 h-4 shrink-0" />
          <span className="font-semibold shrink-0">Support-Modus</span>
          <span className="opacity-90 truncate">
            {session.targetOrgName} · {session.reason}
          </span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/*
            Phase-2-Button: auditierte Schreibaktionen. Lesen ist
            blockiert durch den supabase-Wrapper (siehe `client.ts`);
            schreiben geht nur durch die zwei in der DB hinterlegten
            Whitelist-RPCs, die hier vom Dialog aus aufgerufen werden.
          */}
          <button
            onClick={() => setActionDialogOpen(true)}
            className="flex items-center gap-1.5 rounded-md bg-destructive-foreground/15 hover:bg-destructive-foreground/25 px-3 py-1 font-medium transition-colors"
            aria-label="Support-Aktion ausführen"
          >
            <Wrench aria-hidden="true" className="w-3.5 h-3.5" />
            Aktion…
          </button>
          <button
            onClick={handleEnd}
            disabled={ending}
            className="flex items-center gap-1.5 rounded-md bg-destructive-foreground/15 hover:bg-destructive-foreground/25 px-3 py-1 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {ending ? (
              <Loader2 aria-hidden="true" className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <LogOut aria-hidden="true" className="w-3.5 h-3.5" />
            )}
            Beenden
          </button>
        </div>
      </div>
      <SupportActionDialog open={actionDialogOpen} onOpenChange={setActionDialogOpen} />
    </>
  );
}
